import { useCallback, useEffect, useRef, useState } from 'react'
import { CONTROL_BAR_AUTO_HIDE_DELAY_MS } from '@/constants/video'

/**
 * Manages auto-hide visibility for the player control bar.
 *
 * Rules:
 * - Controls are visible when the video is paused.
 * - Controls are visible briefly after any user interaction (mouse move, touch, keyboard).
 * - Controls hide after CONTROL_BAR_AUTO_HIDE_DELAY_MS of inactivity while playing.
 * - Controls stay visible while the user hovers over the control bar itself.
 */
export function useControlBarVisibility(
  containerRef: React.RefObject<HTMLElement | null>,
  playing: boolean,
): { visible: boolean; onControlBarInteraction: () => void } {
  const [visible, setVisible] = useState(true)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hoveringControlBarRef = useRef(false)

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])

  const scheduleHide = useCallback(() => {
    clearHideTimer()
    if (!playing) return // Never auto-hide when paused
    if (hoveringControlBarRef.current) return // Don't hide while hovering controls

    hideTimerRef.current = setTimeout(() => {
      setVisible(false)
    }, CONTROL_BAR_AUTO_HIDE_DELAY_MS)
  }, [playing, clearHideTimer])

  const showControls = useCallback(() => {
    setVisible(true)
    scheduleHide()
  }, [scheduleHide])

  // Called by the control bar on mouseenter/mouseleave to prevent hiding while interacting.
  const onControlBarInteraction = useCallback(() => {
    showControls()
  }, [showControls])

  // Listen for user activity on the player container.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const onMouseMove = () => showControls()
    const onTouchStart = () => showControls()
    const onKeyDown = () => showControls()

    const onMouseEnterControlBar = () => {
      hoveringControlBarRef.current = true
      clearHideTimer()
    }
    const onMouseLeaveControlBar = () => {
      hoveringControlBarRef.current = false
      scheduleHide()
    }

    container.addEventListener('mousemove', onMouseMove)
    container.addEventListener('touchstart', onTouchStart, { passive: true })
    container.addEventListener('keydown', onKeyDown)

    // Find the control bar element within the container to track hover state.
    // Uses a data attribute so we don't couple to CSS class names.
    const controlBar = container.querySelector('[data-hls-control-bar]')
    controlBar?.addEventListener('mouseenter', onMouseEnterControlBar)
    controlBar?.addEventListener('mouseleave', onMouseLeaveControlBar)

    return () => {
      container.removeEventListener('mousemove', onMouseMove)
      container.removeEventListener('touchstart', onTouchStart)
      container.removeEventListener('keydown', onKeyDown)
      controlBar?.removeEventListener('mouseenter', onMouseEnterControlBar)
      controlBar?.removeEventListener('mouseleave', onMouseLeaveControlBar)
      clearHideTimer()
    }
  }, [containerRef, showControls, scheduleHide, clearHideTimer])

  // When paused, always show. When playback resumes, schedule hide.
  useEffect(() => {
    if (!playing) {
      setVisible(true)
      clearHideTimer()
    } else {
      scheduleHide()
    }
  }, [playing, scheduleHide, clearHideTimer])

  return { visible, onControlBarInteraction }
}
